import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../services/api';

interface IncompleteStop {
  _id: string;
  customerName?: string;
  addressSummary?: string;
  serviceLabel?: string;
  crewName?: string;
  routeDate?: string;
  serviceRequestId?: any;
}

interface IncompleteStopsResolverProps {
  onResolved?: () => void;
}

/**
 * Forces provider to resolve unfinished route stops (next recurring or move day).
 */
const IncompleteStopsResolver: React.FC<IncompleteStopsResolverProps> = ({ onResolved }) => {
  const [stops, setStops] = useState<IncompleteStop[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [active, setActive] = useState<IncompleteStop | null>(null);
  const [action, setAction] = useState<'next_recurring' | 'move'>('next_recurring');
  const [moveDate, setMoveDate] = useState('');
  const [permanent, setPermanent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = (await apiService.get('/service-requests/incomplete-stops')) as any;
      if (res?.success && Array.isArray(res.data)) {
        setStops(res.data);
      } else {
        setStops([]);
        if (res && res.success === false) {
          setLoadError(res.error || 'Could not load incomplete stops');
        }
      }
    } catch (e: any) {
      setStops([]);
      setLoadError(e?.message || 'Could not load incomplete stops');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openResolve = (stop: IncompleteStop) => {
    setActive(stop);
    setAction('next_recurring');
    setMoveDate('');
    setPermanent(false);
    setError(null);
  };

  const submit = async () => {
    if (!active) return;
    setSubmitting(true);
    setError(null);
    try {
      if (action === 'next_recurring') {
        const res = (await apiService.post(
          `/service-requests/incomplete-stops/${active._id}/resolve`,
          { action: 'next_recurring' }
        )) as any;
        if (!res?.success) {
          setError(res?.error || 'Failed to resolve stop');
          setSubmitting(false);
          return;
        }
      } else {
        if (!moveDate) {
          setError('Choose a date to move this stop to.');
          setSubmitting(false);
          return;
        }
        const srId =
          typeof active.serviceRequestId === 'object' && active.serviceRequestId?._id
            ? String(active.serviceRequestId._id)
            : String(active.serviceRequestId || '');
        if (!srId) {
          setError('Missing service request for this stop.');
          setSubmitting(false);
          return;
        }
        const scheduleRes = (await apiService.post(`/service-requests/${srId}/schedule`, {
          scheduledDate: moveDate,
          resetRecurringSchedule: permanent,
        })) as any;
        if (!scheduleRes?.success) {
          setError(scheduleRes?.error || 'Failed to move schedule');
          setSubmitting(false);
          return;
        }
        const resolveRes = (await apiService.post(
          `/service-requests/incomplete-stops/${active._id}/resolve`,
          { action: 'move' }
        )) as any;
        if (!resolveRes?.success) {
          setError(
            resolveRes?.error ||
              'Schedule moved, but could not clear the incomplete alert. Refresh and try again.'
          );
          setSubmitting(false);
          return;
        }
      }
      setActive(null);
      await load();
      onResolved?.();
    } catch (e: any) {
      setError(e?.message || 'Failed to resolve stop');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Alert severity="info" sx={{ mb: 2 }} id="incomplete-stops">
        Checking for incomplete route stops…
      </Alert>
    );
  }

  if (loadError) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        id="incomplete-stops"
        action={
          <Button color="inherit" size="small" onClick={() => load()}>
            Retry
          </Button>
        }
      >
        {loadError}
      </Alert>
    );
  }

  if (stops.length === 0) {
    return null;
  }

  return (
    <>
      <Alert
        severity="warning"
        id="incomplete-stops"
        sx={{
          mb: 3,
          border: '2px solid',
          borderColor: 'warning.main',
          position: 'sticky',
          top: 8,
          zIndex: 10,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Incomplete stops to reschedule ({stops.length})
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          These stops were not completed on their route day. They may not still appear on today&apos;s
          schedule — use <strong>Reschedule</strong> here (next recurring date, or move to another day).
        </Typography>
        <Stack spacing={1}>
          {stops.map((stop) => (
            <Box
              key={stop._id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                bgcolor: 'background.paper',
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {stop.customerName || 'Customer'}
                  {stop.serviceLabel ? ` — ${stop.serviceLabel}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {stop.addressSummary}
                  {stop.crewName ? ` · Crew: ${stop.crewName}` : ''}
                  {stop.routeDate
                    ? ` · Missed route date: ${String(stop.routeDate).slice(0, 10)}`
                    : ''}
                </Typography>
              </Box>
              <Button variant="contained" color="warning" size="small" onClick={() => openResolve(stop)}>
                Reschedule
              </Button>
            </Box>
          ))}
        </Stack>
      </Alert>

      <Dialog open={!!active} onClose={() => !submitting && setActive(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reschedule incomplete stop</DialogTitle>
        <DialogContent>
          {active && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {active.customerName}
              {active.serviceLabel ? ` — ${active.serviceLabel}` : ''}
            </Typography>
          )}
          <RadioGroup
            value={action}
            onChange={(e) => setAction(e.target.value as 'next_recurring' | 'move')}
          >
            <FormControlLabel
              value="next_recurring"
              control={<Radio />}
              label="Next recurring date (skip this visit — no charge)"
            />
            <FormControlLabel
              value="move"
              control={<Radio />}
              label="Move to another day"
            />
          </RadioGroup>

          {action === 'move' && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="New service date"
                type="date"
                fullWidth
                value={moveDate}
                onChange={(e) => setMoveDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <RadioGroup
                value={permanent ? 'permanent' : 'one-time'}
                onChange={(e) => setPermanent(e.target.value === 'permanent')}
              >
                <FormControlLabel
                  value="one-time"
                  control={<Radio />}
                  label="One-time change (keep regular schedule day)"
                />
                <FormControlLabel
                  value="permanent"
                  control={<Radio />}
                  label="Permanent (update recurring schedule day)"
                />
              </RadioGroup>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActive(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IncompleteStopsResolver;
